import { memo, useMemo } from 'react'
import { Table } from 'antd'
import { SearchInput } from '../../components/SearchInput'
import { Button } from '../../components/Button'
import { FormattedRelativeTime } from '../../components/FormattedRelativeTime'
import { formatArrival } from '../../utils/c2cReceiving'
import { Pill } from './Pill'
import { RowActionButton } from './RowActionButton'
import { useNamespacedT } from './useNamespacedT'

const T = 'c2c_receiving.queue'

// Zone B (spec §5.3): the platform's arrived_at_warehouse queue for this warehouse. The
// list is capped at 200 rows server-side; total/has_more make the cap visible. Client-side
// pagination only.
//
// Memoised: the queue re-renders on every keystroke of the scan box otherwise, and its
// columns are rebuilt each time. The container hands it stable callbacks for that reason.
export const QueueTable = memo(
  ({
    orders,
    total,
    hasMore,
    isLoading,
    isFetching,
    isError,
    isForbidden,
    errorMessage,
    onRefresh,
    onSearchChange,
    timeZone,
    canScan,
    onLabelAction,
    canRetryForbidden = false,
    busyOrderId,
    busy,
  }) => {
    const t = useNamespacedT(T)

    const columns = useMemo(
      () => [
        {
          title: t('headers.arrived_at'),
          key: 'arrived_at',
          render: (_, order) => (
            <div className="whitespace-nowrap">
              <div className="text-sm text-gray-900">{formatArrival(order.arrived_at_warehouse_at, timeZone)}</div>
              {order.arrived_at_warehouse_at && (
                <div className="text-xs text-gray-500">
                  <FormattedRelativeTime date={order.arrived_at_warehouse_at} time_zone={timeZone || ''} />
                </div>
              )}
            </div>
          ),
        },
        { title: t('headers.roll_code'), dataIndex: 'roll_code', key: 'roll_code', render: code => <span className="font-mono text-sm">{code}</span> },
        { title: t('headers.order_number'), dataIndex: 'order_number', key: 'order_number', render: n => <span className="text-sm">{n}</span> },
        {
          title: t('headers.receiver'),
          dataIndex: 'receiver_name',
          key: 'receiver_name',
          render: n => <span className="text-sm font-medium">{n || '-'}</span>,
        },
        {
          title: t('headers.destination'),
          dataIndex: 'destination_point_name',
          key: 'destination_point_name',
          render: n => <span className="text-sm">{n || '-'}</span>,
        },
        {
          title: t('headers.carrier'),
          dataIndex: 'destination_carrier_name',
          key: 'destination_carrier_name',
          render: n => <span className="text-sm">{n || '-'}</span>,
        },
        {
          title: t('headers.label'),
          dataIndex: 'has_label',
          key: 'has_label',
          // Same pill as the outcome badge next to it: two shapes for the same kind of
          // information on one row is noise the operator has to re-learn.
          render: hasLabel => (
            <Pill tone={hasLabel ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-100 text-gray-700 border-gray-200'}>
              {t(hasLabel ? 'label_state.existing' : 'label_state.pending')}
            </Pill>
          ),
        },
        {
          title: t('headers.actions'),
          key: 'actions',
          render: (_, order) => (
            <RowActionButton order={order} label={t('generate_label')} canScan={canScan} busy={busy} busyOrderId={busyOrderId} onClick={onLabelAction} />
          ),
        },
      ],
      [t, timeZone, canScan, busy, busyOrderId, onLabelAction],
    )

    // A forbidden warehouse gets the panel and nothing else: the search box and the header's
    // Actualizar would only fire more 403s at a backend that has already given its final
    // answer, and a "(0)" count next to the title reads as an empty queue rather than as no
    // access. One manual retry does belong here, though — a 403 is never retried and the
    // 30 s poll stops, so a permission that lands a minute after login (or a token-refresh
    // race) used to leave the station bricked until someone pressed F5.
    if (isForbidden) {
      return (
        <div className="relative bg-white rounded-lg border border-gray-200 px-4 py-4">
          <div className="p-6 text-gray-500" role="alert">
            <div>{t('forbidden')}</div>
            <div className="mt-1 text-sm">{t('forbidden_hint')}</div>
            {canRetryForbidden && (
              <Button type="secondary" dense className="px-3 mt-3" onClick={onRefresh} disabled={isFetching}>
                {t('retry')}
              </Button>
            )}
          </div>
        </div>
      )
    }

    let body
    if (isError) {
      body = (
        <div className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          <span>
            {t('fetch_error')}
            {errorMessage ? ` ${errorMessage}` : ''}
          </span>
          {/* The same refetch the header's Actualizar runs — one prop, one behaviour. */}
          <Button type="secondary" dense onClick={onRefresh}>
            {t('retry')}
          </Button>
        </div>
      )
    } else {
      body = (
        <>
          {hasMore && (
            <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              {t('truncated', { shown: orders.length, total })}
            </div>
          )}
          {/* The first load rides the Table's own spinner (dataSource is empty until it
              lands). A page-level Loader here is an `absolute inset-0` backdrop-blur that
              would grey out the scan box the operator is meant to use immediately.
              `isLoading` only: the 30 s poll and every post-label invalidation are
              `isFetching`, and a spinner over rows that are already correct — four times a
              minute, and after every scan — makes the queue look broken. Actualizar is the
              control that says a fetch is in flight. */}
          <Table
            rowKey="id"
            columns={columns}
            dataSource={orders}
            loading={isLoading}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
            locale={{ emptyText: t('empty') }}
          />
        </>
      )
    }

    return (
      <div className="relative bg-white rounded-lg border border-gray-200 px-4 py-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('title')} <span className="text-gray-400 font-normal">({total ?? orders.length})</span>
          </h2>
          <div className="flex-1 min-w-[16rem]">
            <SearchInput inputValue="" debounce={300} onChange={onSearchChange} placeholder={t('search_placeholder')} />
          </div>
          <Button type="secondary" onClick={onRefresh} disabled={isFetching}>
            {t('refresh')}
          </Button>
        </div>
        {body}
      </div>
    )
  },
)
